import React, { useState } from 'react';
import { Image, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { getFullFileUrl } from '../../../../../../../Common/Uploaders/ImageUpload';
import EmptyImage from '../../Empty';
import { MediaDataKey } from '../../index';
import UploadUserMediaModal, { MediaMeasureType } from '../UploadMediaModal';
import { Participant, useUserMarathonMediaDelete } from '../../../../../../../../hooks/api/user';

import styles from '../index.module.scss';
import { useSimpleModal } from '../../../../../../../Common/Modal/Simple';
import { useUserProfileProvider } from '../../../../../../../../context/userProfile';
import { capitalizeFirstLetter } from '../../../../../../../../utils';

export interface UserMarathonThumbnailProps {
  media?: string;
  editProps?: {
    withEdit: boolean;
    dataKey: MediaDataKey | undefined;
    measure: MediaMeasureType;
  };
  selectedMarathon?: Participant;
}

export default function UserMarathonThumbnail({
  media = undefined, editProps, selectedMarathon,
}: UserMarathonThumbnailProps) {
  const [modalOpen, setModalOpen] = useState(false);

  /** Deletion logic */
  const { id: userId = '' } = useParams<{ id: string; }>();
  const { userById } = useUserProfileProvider();
  const { open: openConfirm, contextHolder } = useSimpleModal();

  const requestUrl = editProps && selectedMarathon?.id
    ? `${userId}/participants/${selectedMarathon.id}/measure${
      capitalizeFirstLetter(editProps.measure)}/${editProps.dataKey}` : '';
  const userMarathonImageDelete = useUserMarathonMediaDelete(requestUrl);

  /** General */
  const handleOpen = (open: boolean) => {
    setModalOpen(open);
  };

  if (!editProps?.withEdit) {
    return (
      media ? <Image src={getFullFileUrl(media)} preview={false} /> : <EmptyImage />
    );
  }

  // e: React.MouseEvent<HTMLElement>
  const handleDelete = () => {
    openConfirm({
      title: 'Удалить медиа-файл?',
      content: 'Вы уверены, что хотите удалить этот медиа-файл?',
      cancelText: 'Отмена',
      centered: true,
      okText: 'Удалить',
      okButtonProps: {
        type: 'primary',
        danger: true,
      },
      onOk: () => userMarathonImageDelete.fetch(
        undefined,
        requestUrl,
      ).then(() => {
        message.success('Медиа успешно удалено!');
        userById.fetch(undefined, userId);
      }),
      maskClosable: true,
    });
  };

  /** If withEdit then render edit icon and modal for edit image */
  return (
    <>
      <UploadUserMediaModal
        variant="image"
        media={media}
        dataKey={editProps.dataKey}
        open={modalOpen}
        handleOpen={handleOpen}
        measure={editProps.measure}
        participantId={selectedMarathon?.id || ''}
      />
      <div className={styles.mediaThumbnailContainer}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div onClick={(e) => e.stopPropagation()}>
          {contextHolder}
        </div>
        {media && (<DeleteButton onClick={() => handleDelete()} />)}
        <EditButton onClick={() => handleOpen(true)} />

        {media ? <Image src={getFullFileUrl(media)} preview={false} /> : <EmptyImage />}
      </div>
    </>
  );
}

export function DeleteButton({ onClick, className }: { onClick: () => void, className?: string}) {
  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label="edit-marathon-img"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick();
      }}
      className={clsx(styles.editButton, className)}
      style={{ right: '30px' }}
    >
      <DeleteOutlined className={styles.icon} style={{ stroke: 'white', strokeWidth: 2 }} />
    </span>
  );
}

export function EditButton({ onClick, className }: { onClick: () => void, className?: string}) {
  return (
    <span
      role="button"
      tabIndex={-1}
      aria-label="edit-marathon-img"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick();
      }}
      className={clsx(styles.editButton, className)}
    >
      <EditOutlined className={styles.icon} style={{ stroke: 'white', strokeWidth: 2 }} />
    </span>
  );
}
